import React from 'react';
import Link from 'next/link';

const SimilarIcons = ({ slug, collections = [], initialData = null }) => {
  // Collection URL mappings remain the same
  const collectionUrlMap = {
    basic: {
      baseUrl: 'https://icons-pbl.davooda.com/basic-icons/',
      imageSuffix: '.svg',
      type: 'svg',
      urlPrefix: '/basic-icons',
    },
    outline: {
      baseUrl: 'https://icons-pbl.davooda.com/outline/png-256px/',
      imageSuffix: '-256x256.png',
      type: 'png',
      urlPrefix: '/outline',
    },
    'polish-blue': {
      baseUrl: 'https://icons-pbl.davooda.com/polish-blue/png-256px/',
      imageSuffix: '-256x256.png',
      type: 'png',
      urlPrefix: '/polish-blue-icons',
    },
    'basic3d': {
      baseUrl: 'https://icons-pbl.davooda.com/basic3d-icons/png-256px/',
      imageSuffix: '.png',
      type: 'png',
      urlPrefix: '/basic3d',
    },
  };

  // Process data function remains mostly the same
  const processData = () => {
    // Your existing code here
    if (!initialData) {
      return {};
    }

    let items = [];
    if (Array.isArray(initialData)) {
      items = initialData;
    } else if (initialData.items && Array.isArray(initialData.items)) {
      items = initialData.items;
    } else {
      return {};
    }

    if (items.length === 0) {
      return {};
    }

    const iconsGroupedByCollection = {};

    items.forEach((item) => {
      if (!item) return;
      const collection = item.collection || 'Unknown';
      const isProt = collection.startsWith('prot-');
      const mapKey = isProt ? `prot-${collection.split('prot-')[1]}` : collection;

      if (!iconsGroupedByCollection[collection]) {
        iconsGroupedByCollection[collection] = [];
      }

      let src = '';
      if (collectionUrlMap[mapKey]) {
        src = `${collectionUrlMap[mapKey].baseUrl}${item.slug}${collectionUrlMap[mapKey].imageSuffix}`;
      } else if (item.url_small) {
        src = item.url_small;
      } else {
        src = '/images/placeholder.png';
      }

      let url = '/icons';
      if (collection === 'basic3d') {
        url = `/basic3d/${item.slug}`;
      } else if (collectionUrlMap[mapKey] && collectionUrlMap[mapKey].urlPrefix) {
        url = `${collectionUrlMap[mapKey].urlPrefix}/${item.slug}`;
      } else {
        url = item.media_type === 'icon' ? `/icon/${item.slug}` : `/photo/${item.slug}`;
      }

      const width =
        mapKey === 'outline' || mapKey === 'polish-blue'
          ? 256
          : item.url_small_width || 100;
      const height =
        mapKey === 'outline' || mapKey === 'polish-blue'
          ? 256
          : item.url_small_height || 100;

      iconsGroupedByCollection[collection].push({
        src: src,
        width: width,
        height: height,
        alt: item.title || item.slug || 'Similar Icon',
        key: item.slug || `icon-${Math.random().toString(36).substr(2, 9)}`,
        url: url,
        isFree: item.is_free,
        collection: collection,
      });
    });

    return iconsGroupedByCollection;
  };

  // Process the data once
  const iconsByCollection = processData();
  const hasCollections = Object.keys(iconsByCollection).length > 0;

  // Function to determine if an icon is from the basic collection
  const isBasicIcon = (collectionName) => {
    return collectionName === 'basic';
  };
  
  // Function to get the correct collection URL
  const getCollectionUrl = (collectionName) => {
    if (collectionName === 'basic3d') {
      return '/basic3d/';
    } else {
      return `/${collectionName}-icons/`;
    }
  };

  return (
    <div className="bg-transparent mt-4">
      <style jsx>{`
        .basic3d-icon {
          max-width: 65% !important;
          max-height: 65% !important;
          width: 65% !important;
          height: 65% !important;
          transform: scale(1.3);
        }
        .standard-icon {
          max-width: 50%;
          max-height: 50%;
        }
        .basic-svg-icon {
          width: 50%;
          height: 50%;
          object-fit: contain;
        }
        .basic3d-container {
          padding: 0 !important;
        }
        .icon-container {
          display: flex;
          align-items: center;
          justify-content: center;
        }
      `}</style>

      {collections.length > 0 && hasCollections ? (
        <>
          {collections.map((collectionName, collectionIndex) => {
            const icons = iconsByCollection[collectionName];
            if (Array.isArray(icons) && icons.length > 0) {
              const displayName = collectionName
                .split('-')
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');

              const isBasic3d = collectionName === 'basic3d';
              const isBasicSvg = isBasicIcon(collectionName);
              
              // Ограничиваем количество иконок: 30 для первой коллекции, 10 для остальных
              const limitedIcons = collectionIndex === 0 
                ? icons.slice(0, 30) 
                : icons.slice(0, 10);

              return (
                <div key={collectionName} className="mb-4">
                  <Link href={getCollectionUrl(collectionName)}>
                    <p className="text-sm mb-2 mt-0 inline-block underline">{displayName}</p>
                  </Link>
                  <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-7 lg:grid-cols-10 gap-2">
                    {limitedIcons.map((icon) => (
                      <div key={icon.key} className="rounded-lg">
                        <Link href={icon.url}>
                          <div className={`icon-container ${isBasic3d ? 'basic3d-container p-0' : 'p-1'} rounded-lg border border-gray-200 aspect-square group transition-transform duration-300`}>
                            {isBasicSvg ? (
                              // Special handling for basic SVG icons
                              <div className="w-full h-full flex items-center justify-center">
                                <img
                                  src={icon.src}
                                  alt={icon.alt}
                                  className="basic-svg-icon group-hover:scale-110 transition-transform duration-300"
                                />
                              </div>
                            ) : (
                              // Standard handling for other icons
                              <img
                                src={icon.src}
                                alt={icon.alt}
                                width="50"
                                height="50"
                                className={`${isBasic3d ? 'basic3d-icon' : 'standard-icon'} group-hover:scale-110 transition-transform duration-300`}
                                style={{
                                  maxWidth: isBasic3d ? '65% !important' : '50%',
                                  maxHeight: isBasic3d ? '65% !important' : '50%',
                                  width: isBasic3d ? '65% !important' : 'auto',
                                  height: isBasic3d ? '65% !important' : 'auto',
                                }}
                              />
                            )}
                          </div>
                        </Link>
                      </div>
                    ))}
                  </div>
                </div>
              );
            }
            return null;
          })}
        </>
      ) : (
        <div className="text-gray-500 py-4">No similar icons available.</div>
      )}
    </div>
  );
};

export default SimilarIcons;